/*
 * VNCmail : A whole new experience in enterprise email communication.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Component, HostListener, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { debounceTime, Observable, of, Subject, switchMap, take, takeUntil } from "rxjs";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Store } from "@ngrx/store";
import { VNCInputComponent } from "@uxf/vnc-library";
import { SelectionModel } from "@angular/cdk/collections";
import { FileInfo, OwnCloudService } from "src/app/shared/owncloud.service";
import { FormControl } from "@angular/forms";
import { MailUtils } from "src/app/mail/utils/mail-utils";
import { environment } from "src/environments/environment";
import { ConfigService } from "src/app/config.service";
import { ElectronService } from "src/app/services/electron.service";
import { DatabaseService } from "src/app/services/db/database.service";
import { RootState, getVNCContacts } from "src/app/reducers";
import { CommonUtils } from "src/app/common/utils/common-util";

@Component({
  selector: "vp-owncloud-share",
  templateUrl: "./owncloud-share.html"
})
export class OwncloudShareDialogComponent implements OnInit, OnDestroy {
  searchControl: FormControl = new FormControl("", []);
  contacts = [];
  permission = "31";
  defaultAvatar = "";
  filteredOptions$: Observable<any[]>;
  selection = new SelectionModel<any>(true, []);
  file: FileInfo;
  alreadyShared = [];
  isEdit: boolean = false;
  @ViewChild(VNCInputComponent) input: VNCInputComponent;
  private isAlive$ = new Subject<boolean>();

  constructor(
    private dialog: MatDialogRef<OwncloudShareDialogComponent>,
    private electronService: ElectronService,
    private dbService: DatabaseService,
    private configService: ConfigService,
    private ownCloudService: OwnCloudService,
    private store: Store<RootState>,
    @Inject(MAT_DIALOG_DATA) public data: any) {
      this.file = this.data.file;
      if (!!this.file.shareWithId) {
        this.permission = this.file.permissions.toString();
      }
      this.defaultAvatar = CommonUtils.getFullUrl("/assets/icon/groupchat.svg");
      this.ownCloudService.getFilesSharing(this.file.path).pipe(take(1)).subscribe(s => {
        this.alreadyShared = s.map(e => (!!e.shareWithEmailOrJid) ? e.shareWithEmailOrJid : e.shareWithDisplayName);
      });
  }

  getAvatarUrl(bare: string): string {
    let avatarUrl = bare;
    const storedAvatarUrl = this.dbService.avatar[bare];
    if (!!storedAvatarUrl) {
      return storedAvatarUrl;
    } else {
      const avatarId = (environment.isElectron) ? this.electronService.md5(bare) : md5(bare);
      avatarUrl = `${this.configService.avatarURL}/${avatarId}.jpg`;
    }
    return avatarUrl;
  }

  filterOptions(keyword): Observable<any[]> {
    const subject = new Subject<any[]>();

    if (!keyword) {
      subject.next([]);
    } else {
      const itemType = (this.file.type === "dir") ? "folder" : "file";
      this.ownCloudService.getFilesSharees(keyword, itemType).pipe(take(1)).subscribe(r => {
        const allOptions = r.map(e => {
          return (e.shareType === 0) ? { ...e, imgSrc: this.getAvatarUrl(e.email) } : { ...e, imgSrc: this.defaultAvatar };
        });
        allOptions.sort((a, b) => {
          let c = a.fullName.toLowerCase();
          let d = b.fullName.toLowerCase();
          return c < d ? -1 : c > d ? 1 : 0;
        });
        const allSelectable = allOptions.filter(o => this.alreadyShared.indexOf((!!o.email) ? o.email : o.fullName) < 0);
        subject.next(allSelectable);
      });
    }
    return subject.asObservable().pipe(take(1));
  }

  selectUser(data) {
    if (!this.selection.isSelected(data)) {
      this.selection.select(data);
    }
    this.searchControl.patchValue("");
    this.input.autocomplete.closePanel();
  }

  removeUser(data) {
    this.selection.deselect(data);
  }

  ngOnInit() {
    this.filteredOptions$ = this.searchControl.valueChanges.pipe(debounceTime(200)
    , switchMap(keyword => this.filterOptions(keyword)));
  }

  ngOnDestroy() {
    this.isAlive$.next(false);
    this.isAlive$.complete();
  }

  @HostListener("document:keydown.esc")
  cancel(): void {
    this.dialog.close();
  }

  get isMobileSize () {
    return MailUtils.isMobileSize();
  }

  async share() {
    this.dialog.close({ shared: true, permission: this.permission, users: this.selection.selected });
  }

}
